<script setup lang="ts">
import type { SizeVariants } from '~kui/types/kui';

interface Props {
  backgroundColor?: string;
  rounding?: Extract<SizeVariants, 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'> | 'full' | 'none';
  negativeMargin?: Extract<SizeVariants, '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'> | 'none';
  containerSize?: 'none' | 'full';
  blurSize?: Extract<SizeVariants, '2xs' | 'xs'>;
  /**
   * `span` keeps the plate phrasing content, for use inside a `<button>`. The caller then sets the
   * root's display through its class, as a span root is otherwise inline.
   */
  as?: 'div' | 'span';
}

const props = withDefaults(defineProps<Props>(), {
  containerSize: 'none',
  backgroundColor: 'bg-white',
  rounding: 'lg',
  negativeMargin: 'sm',
  blurSize: 'xs',
  as: 'div',
});

const classList = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        blurSize: {
          '2xs': 'blur-[2px]',
          'xs': 'blur-xs',
        },
        rounding: {
          'none': '',
          'sm': 'rounded-sm',
          'md': 'rounded-md',
          'lg': 'rounded-lg',
          'xl': 'rounded-xl',
          '2xl': 'rounded-2xl',
          '3xl': 'rounded-3xl',
          '4xl': 'rounded-4xl',
          'full': 'rounded-full',
        },
        negativeMargin: {
          'none': '',
          '2xs': '-m-0',
          'xs': '-m-1',
          'sm': '-m-2',
          'md': '-m-4',
          'lg': '-m-6',
          'xl': '-m-8',
        },
      },
    })({
      rounding: props.rounding,
      negativeMargin: props.negativeMargin,
      blurSize: props.blurSize,
    }),
    props.backgroundColor,
  );
});

const containerSize = props.containerSize === 'full' ? 'size-full' : ''
</script>

<template>
  <component :is="as" class="relative">
    <component
      :is="as"
      class="absolute inset-0 blur-xs"
      :class="classList"
    />
    <component
      :is="as"
      class="relative"
      :class="[containerSize, { block: as === 'span' }]"
    >
      <slot />
    </component>
  </component>
</template>
